import { FC, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import ArrowBlackIcon from "assets/icons/arrow_black.svg?react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Navigation } from "swiper";
import BangerGames from "assets/icons/banger/banger_games.webp";
import GloryGames from "assets/icons/glory/glory_games.webp";
import type { Swiper as SwiperType } from "swiper";
import { NewCategoryType, setCategory } from "../../../redux/game/slice";
import useTranslate from "../../../hooks/useTranslate";
import { CATEGORIES_ENUMS } from "../../../constants/navigation/categories";
import "./styles.sass";
import { useGetGamesByCategory } from "../../../queries/useGetGamesByCategory";
import GameCardContainer from "../../../containers/molecules/GameCardContainer";
import { usePlatform } from "../../../hooks/usePlatform";
import "swiper/css";
import "swiper/css/navigation";
import { makeLangUrl } from "../../../helpers/makeLangUrl";
import url from "../../../url";
import { useIntersectionObserver } from "../../../hooks/useInViewPort";

const { VITE_APP_PROJECT_NAME } = import.meta.env;

type CategoryItemProps = {
  item: NewCategoryType;
};
export const CategoryItem: FC<CategoryItemProps> = ({ item }) => {
  const t = useTranslate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentIcon =
    VITE_APP_PROJECT_NAME === "banger" ? BangerGames : GloryGames;
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  const [isLocked, setIsLocked] = useState(false);
  const prevRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLDivElement | null>(null);
  const { ref: sentinelRef, inView } = useIntersectionObserver<HTMLDivElement>({
    root: null,
    rootMargin: "200px 0px",
    threshold: 0,
    freezeOnceVisible: true,
  });
  const { games, total } = useGetGamesByCategory({
    take: 15,
    categories: item.name,
    platform: usePlatform(),
    inView,
  });
  const uniqueId = item.name.toLowerCase().replace(/\s+/g, "-");

  const handleCategory = () => {
    if (item.name === CATEGORIES_ENUMS.LOBBY) {
      dispatch(setCategory(CATEGORIES_ENUMS.LOBBY));
      navigate(makeLangUrl(url.routes.casino.index()));
    } else {
      dispatch(setCategory(item.name));
      navigate(makeLangUrl(url.routes.casino.category(item.name)));
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (swiper) {
      swiper.update();
    }
  }, [swiper, games.length, inView]);

  return (
    <div className="CategoryItem" ref={sentinelRef}>
      <div className="CategoryItem__header">
        <div className="CategoryItem__header_icon">
          <img src={item?.pathToImg || currentIcon} alt="icon" />
        </div>
        <span>{t(item.translatePlaceholder)}</span>
        <button
          type="button"
          className="CategoryItem__header_button"
          onClick={handleCategory}
        >
          {t("bonus.title.all.games")}
        </button>
      </div>
      <div className="CategoryItem__slider">
        <div
          ref={prevRef}
          className={`CategoryItem__slider_arrow CategoryItem__slider_arrow--prev swiper-button-prev-${uniqueId}`}
        >
          <ArrowBlackIcon />
        </div>
        <div
          ref={nextRef}
          className={`CategoryItem__slider_arrow CategoryItem__slider_arrow--next swiper-button-next-${uniqueId}`}
        >
          <ArrowBlackIcon />
        </div>
        {inView ? (
          <Swiper
            modules={[Navigation]}
            slidesPerView="auto"
            spaceBetween={16}
            watchOverflow
            onBeforeInit={(s) => {
              // @ts-ignore
              // eslint-disable-next-line no-param-reassign
              s.params.navigation = {
                // @ts-ignore
                ...(s.params.navigation ?? {}),
                prevEl: prevRef.current,
                nextEl: nextRef.current,
                lockClass: "swiper-button-lock",
              };
            }}
            onInit={(s) => {
              setSwiper(s);
              s.navigation.init();
              s.navigation.update();

              setIsLocked(s.isLocked);
              s.on("lock", () => setIsLocked(true));
              s.on("unlock", () => setIsLocked(false));
              s.on("slidesLengthChange", () => {
                s.update();
              });
              s.on("resize", () => {
                s.update();
              });
            }}
          >
            {games.map((game) => {
              return (
                <SwiperSlide key={game.uuid}>
                  <GameCardContainer game={game} />
                </SwiperSlide>
              );
            })}
            {swiper && !isLocked && (
              <SwiperSlide key="last">
                <div className="CategoryItem__slider_last">
                  <div className="CategoryItem__slider_last_content">
                    <span className="CategoryItem__slider_last_content_first">
                      {t("casino.lobby.see.all")}
                    </span>
                    <span className="CategoryItem__slider_last_content_second">
                      {total} {t("casino.providers.games")}
                    </span>
                    <button type="button" onClick={handleCategory}>
                      {t("casino.lobby.see")}
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            )}
          </Swiper>
        ) : (
          <div style={{ height: "200px", width: "200px" }} />
        )}
      </div>
    </div>
  );
};
